// src/CalendarView.js
import React, { useEffect, useMemo, useState } from 'react';
import api from './apiClient'; // Use centralized client
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import PersonIcon from '@mui/icons-material/Person';
import FlagIcon from '@mui/icons-material/Flag';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const STATUS_COLORS = {
  'to-do': '#7C3AED',
  'in-progress': '#F59E0B',
  completed: '#10B981',
  closed: '#94A3B8',
};

const PRIORITY_COLORS = {
  low: '#22C55E',
  medium: '#F97316',
  high: '#EF4444',
};

function CalendarView({
  userId,
  tasks = [],
  onEventClick, // optional
  onNavigateToList, // optional
}) {
  const [rawTasks, setRawTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateField, setDateField] = useState('due_date'); // 'due_date' | 'target_date'
  const [countMode, setCountMode] = useState('status'); // 'status' | 'assignee' | 'priority'
  const [view, setView] = useState('month');
  const [visibleRange, setVisibleRange] = useState(null);

  useEffect(() => {
    if (!tasks?.length && userId) {
      fetchTasks();
    }
  }, [userId, tasks]);

  const fetchTasks = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/api/tasks?user_id=${userId}`);
      setRawTasks(response.data || []);
    } catch (err) {
      console.error('Error fetching tasks for calendar:', err);
      setError(err.response?.data?.error || 'Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  };

  const sourceTasks = tasks?.length ? tasks : rawTasks;

  const buildEventsFromTasks = (list = []) => {
    const safeDate = (val) => {
      if (!val) return null;
      const d = new Date(val);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    return list
      .map((task) => {
        const date = safeDate(task[dateField]);
        if (!date) return null;
        return {
          title: task.title,
          start: date,
          end: date,
          allDay: true,
          resource: task,
        };
      })
      .filter(Boolean);
  };

  useEffect(() => {
    setEvents(buildEventsFromTasks(sourceTasks));
  }, [sourceTasks, dateField]);

  const handleSelectEvent = (event) => {
    if (onEventClick) onEventClick(event.resource);
    if (onNavigateToList) {
      onNavigateToList({
        dateField,
        date: event.start,
        filterType: 'status',
        filterValue: event.resource?.status || 'to-do',
      });
    }
  };

  const summary = useMemo(() => {
    const counts = {};
    sourceTasks.forEach((t) => {
      const dateVal = t[dateField];
      if (!dateVal) return;

      if (countMode === 'status') {
        const key = (t.status || 'to-do').toLowerCase();
        counts[key] = (counts[key] || 0) + 1;
      } else if (countMode === 'assignee') {
        const key = t.assigned_to ? String(t.assigned_to) : 'unassigned';
        counts[key] = (counts[key] || 0) + 1;
      } else if (countMode === 'priority') {
        const key = (t.priority || 'medium').toLowerCase();
        counts[key] = (counts[key] || 0) + 1;
      }
    });

    const toArray = Object.entries(counts).map(([key, value]) => {
      if (countMode === 'status') {
        const color = STATUS_COLORS[key] || '#94A3B8';
        return { key, label: key.replace(/-/g, ' '), value, color, icon: <CircleIcon fontSize="small" /> };
      }
      if (countMode === 'assignee') {
        const color = '#0EA5E9';
        const label = key === 'unassigned' ? 'Unassigned' : `User ${key}`;
        return { key, label, value, color, icon: <PersonIcon fontSize="small" /> };
      }
      const color = PRIORITY_COLORS[key] || PRIORITY_COLORS.medium;
      return {
        key,
        label: key.replace(/^\w/, (c) => c.toUpperCase()),
        value,
        color,
        icon: <FlagIcon fontSize="small" />,
      };
    });

    return toArray.sort((a, b) => b.value - a.value);
  }, [sourceTasks, dateField, countMode]);

  const eventPropGetter = (event) => {
    const status = (event.resource?.status || 'to-do').toLowerCase();
    const priority = (event.resource?.priority || 'medium').toLowerCase();
    const statusColor = STATUS_COLORS[status] || STATUS_COLORS['to-do'];
    const priorityColor = PRIORITY_COLORS[priority] || PRIORITY_COLORS.medium;

    return {
      style: {
        background: `linear-gradient(135deg, ${statusColor} 0%, ${priorityColor} 100%)`,
        border: 'none',
        color: '#fff',
        borderRadius: 12,
        boxShadow: '0 10px 22px rgba(15,23,42,0.18)',
      },
    };
  };

  const handleSummaryClick = (item) => {
    if (!onNavigateToList) return;
    onNavigateToList({
      dateField,
      dateRange: visibleRange,
      filterType: countMode,
      filterValue: item.key,
    });
  };

  const handleRangeChange = (range) => {
    // range can be array (month) or object (week/day)
    if (Array.isArray(range)) {
      const start = range[0];
      const end = range[range.length - 1];
      setVisibleRange({ start, end });
    } else if (range?.start && range?.end) {
      setVisibleRange({ start: range.start, end: range.end });
    } else {
      setVisibleRange(null);
    }
  };

  const components = {
    event: ({ event }) => {
      const status = (event.resource?.status || 'to-do').toLowerCase();
      const priority = (event.resource?.priority || 'medium').toLowerCase();
      const statusLabel =
        { 'to-do': 'To-Do', 'in-progress': 'In Progress', completed: 'Completed', closed: 'Closed' }[
          status
        ] || 'Task';

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {event.title}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={statusLabel}
              sx={{
                bgcolor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                height: 22,
              }}
            />
            <Chip
              size="small"
              label={(priority || 'medium').replace(/^\w/, (c) => c.toUpperCase())}
              sx={{
                bgcolor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                height: 22,
              }}
            />
          </Stack>
        </Box>
      );
    },
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        p: 3,
        bgcolor: '#f4fbf8',
        border: '1px solid rgba(15,118,110,0.08)',
        boxShadow: '0 18px 36px rgba(15,23,42,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A' }}>
          Task Calendar
        </Typography>
        <Typography variant="body2" sx={{ color: '#0F766E' }}>
          Drag the calendar toolbar to jump weeks/months
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
            Date basis
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={dateField}
            exclusive
            onChange={(_, val) => val && setDateField(val)}
          >
            <ToggleButton value="due_date">Due date</ToggleButton>
            <ToggleButton value="target_date">Target date</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
            Count by
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={countMode}
            exclusive
            onChange={(_, val) => val && setCountMode(val)}
          >
            <ToggleButton value="status">Status</ToggleButton>
            <ToggleButton value="assignee">Assigned</ToggleButton>
            <ToggleButton value="priority">Priority</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
        {summary.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No tasks with a {dateField === 'due_date' ? 'due' : 'target'} date yet.
          </Typography>
        )}
        {summary.map((item) => (
          <Chip
            key={`${countMode}-${item.key}`}
            icon={item.icon}
            label={`${item.label}: ${item.value}`}
            onClick={() => handleSummaryClick(item)}
            sx={{
              bgcolor: `${item.color}1a`,
              color: '#0F172A',
              border: `1px solid ${item.color}`,
              fontWeight: 700,
            }}
          />
        ))}
      </Stack>

      {loading ? (
        <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      ) : (
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 560, backgroundColor: '#fff', borderRadius: 12 }}
          onSelectEvent={handleSelectEvent} // Updated
          eventPropGetter={eventPropGetter}
          components={components}
          views={['month', 'week', 'day']}
          view={view}
          onView={(v) => setView(v)}
          onRangeChange={handleRangeChange}
        />
      )}
    </Box>
  );
}

export default CalendarView;
